import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '../components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { companiesApi } from '../lib/api';
import { toast } from 'sonner';

export default function CreateCompanyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    brand_color: '#0d9488',
    notes: '',
    timezone: 'Europe/Istanbul',
  });

  const mutation = useMutation({
    mutationFn: (data) => companiesApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Company created successfully');
      navigate(`/companies/${res.data._id}`);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    },
  });

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'name' && !prev.slugManual) {
        updated.slug = value.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s]+/g, '-');
      }
      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    mutation.mutate(form);
  };

  return (
    <AppShell
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Companies', href: '/companies' },
        { label: 'New Company' },
      ]}
    >
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl">
        <h1 className="text-xl md:text-2xl font-semibold font-display tracking-tight mb-6">Create Company</h1>
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g. IKM Digital"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, slug: e.target.value, slugManual: true }));
                  }}
                  placeholder="e.g. ikm-digital"
                  data-testid="company-slug-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of the company"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand_color">Brand Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.brand_color}
                      onChange={(e) => handleChange('brand_color', e.target.value)}
                      className="w-10 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={form.brand_color}
                      onChange={(e) => handleChange('brand_color', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    value={form.timezone}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    placeholder="Europe/Istanbul"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Internal notes"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={mutation.isPending} data-testid="company-create-submit-button">
                  {mutation.isPending ? 'Creating...' : 'Create Company'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => navigate('/companies')}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
